import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useTranslation } from "../../hooks/useTranslation";
import { useScope } from "../../hooks/useScope";
import { useIsSubscribed } from "../../hooks/useIsSubscribed";
import { getBankTier } from "../../data/banks";
import {
  getUserBusinessEntities,
  createBusinessEntity,
  updateBusinessEntity,
  deleteBusinessEntity,
  getUserCreditCards,
  createCreditCard,
  updateCreditCard,
  deleteCreditCard,
} from "../../services/creditCardService";
import CardList from "./CardList";
import CardForm from "./CardForm";
import BusinessEntityForm from "./BusinessEntityForm";
import KebabMenu from "./KebabMenu";
import Modal from "../common/Modal";
import "./BusinessCards.css";

const BusinessCards = () => {
  const { currentUser } = useAuth();
  const { isSubscribed, loading: subscriptionLoading } = useIsSubscribed();
  const t = useTranslation();
  const history = useHistory();
  const { isClientView, isManaged, mentorId, effectiveUserId, actualUserId } =
    useScope();
  const [entities, setEntities] = useState([]);
  const [selectedEntityId, setSelectedEntityId] = useState(null);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [showEntityForm, setShowEntityForm] = useState(false);
  const [showCardForm, setShowCardForm] = useState(false);
  const [editingEntity, setEditingEntity] = useState(null);
  const [editingCard, setEditingCard] = useState(null);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterTier, setFilterTier] = useState("");
  const [sortBy, setSortBy] = useState("dateCreated");
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    show: false,
    type: "",
    id: null,
  });

  // Compute scope values
  const isMentorMasquerade = isManaged && !isClientView;
  const isInClientView = isManaged && isClientView;

  useEffect(() => {
    if (currentUser && isSubscribed && !subscriptionLoading) {
      loadEntities();
    }
  }, [
    currentUser,
    isSubscribed,
    subscriptionLoading,
    isClientView,
    isManaged,
    mentorId,
    effectiveUserId,
  ]);

  useEffect(() => {
    if (selectedEntityId && isSubscribed && !subscriptionLoading) {
      loadCards(selectedEntityId);
    } else {
      setCards([]);
    }
  }, [selectedEntityId, currentUser, isSubscribed, subscriptionLoading]);

  const loadEntities = async () => {
    if (!currentUser) return;

    setLoading(true);
    setError(null);
    try {
      // Build scope options based on current view mode
      let queryUserId = currentUser.uid;
      let scopeOptions = { personalOnly: true };

      if (isMentorMasquerade && effectiveUserId) {
        queryUserId = effectiveUserId;
        scopeOptions = { mentorId: actualUserId };
      } else if (isInClientView && mentorId) {
        queryUserId = currentUser.uid;
        scopeOptions = { mentorId };
      }

      const userEntities = await getUserBusinessEntities(
        queryUserId,
        scopeOptions
      );
      setEntities(userEntities);
      // Auto-select first entity if available
      if (userEntities.length > 0 && !selectedEntityId) {
        setSelectedEntityId(userEntities[0].id);
      }
    } catch (err) {
      console.error("Error loading entities:", err);
      setError(t("creditCardsInventory.failedToLoadEntities"));
    } finally {
      setLoading(false);
    }
  };

  const loadCards = async (entityId) => {
    if (!currentUser || !entityId) return;

    setCardsLoading(true);
    try {
      // Build scope options based on current view mode
      let queryUserId = currentUser.uid;
      let scopeOptions = { personalOnly: true };

      if (isMentorMasquerade && effectiveUserId) {
        queryUserId = effectiveUserId;
        scopeOptions = { mentorId: actualUserId };
      } else if (isInClientView && mentorId) {
        queryUserId = currentUser.uid;
        scopeOptions = { mentorId };
      }

      const entityCards = await getUserCreditCards(
        queryUserId,
        "business",
        entityId,
        scopeOptions
      );
      setCards(entityCards);
    } catch (err) {
      console.error("Error loading cards:", err);
      setError(t("creditCardsInventory.failedToLoad"));
    } finally {
      setCardsLoading(false);
    }
  };

  const handleAddEntity = () => {
    setEditingEntity(null);
    setShowEntityForm(true);
  };

  const handleEditEntity = (entity) => {
    setEditingEntity(entity);
    setShowEntityForm(true);
  };

  const handleDeleteEntity = async (entityId) => {
    if (!window.confirm(t("creditCardsInventory.deleteEntityConfirm"))) {
      return;
    }

    try {
      // First, delete all cards for this entity
      const entityCards = await getUserCreditCards(
        currentUser.uid,
        "business",
        entityId
      );
      for (const card of entityCards) {
        await deleteCreditCard(card.id);
      }

      // Then delete the entity
      await deleteBusinessEntity(entityId);

      // Reload entities and reset selection
      await loadEntities();
      if (selectedEntityId === entityId) {
        setSelectedEntityId(null);
        setCards([]);
      }
    } catch (err) {
      console.error("Error deleting entity:", err);
      setError(t("creditCardsInventory.failedToDeleteEntity"));
    }
  };

  const handleEntityFormSubmit = async (entityData) => {
    if (!currentUser) return;

    try {
      if (editingEntity) {
        await updateBusinessEntity(editingEntity.id, entityData);
      } else {
        // Pass mentorId when creating entities in managed scope
        // Mentor masquerade: use actualUserId (mentor's ID)
        // Client view: use mentorId (viewing mentor's ID)
        // Personal view: null
        const entityMentorId = isMentorMasquerade
          ? actualUserId
          : isInClientView && mentorId
          ? mentorId
          : null;
        
        // Use effectiveUserId for the entity owner in masquerade mode
        const entityOwnerUserId = isMentorMasquerade ? effectiveUserId : currentUser.uid;
        
        const newEntityId = await createBusinessEntity(
          entityOwnerUserId,
          entityData,
          entityMentorId
        );
        setSelectedEntityId(newEntityId);
      }
      setShowEntityForm(false);
      setEditingEntity(null);
      await loadEntities();
    } catch (err) {
      console.error("Error saving entity:", err);
      throw err;
    }
  };

  const handleEntityFormCancel = () => {
    setShowEntityForm(false);
    setEditingEntity(null);
  };

  const handleAddCard = () => {
    if (!selectedEntityId) {
      if (entities.length > 0) {
        // Auto-select first entity if available
        setSelectedEntityId(entities[0].id);
        // Wait a moment for state to update, then show form
        setTimeout(() => {
          setEditingCard(null);
          setShowCardForm(true);
        }, 100);
      } else {
        setError(t("creditCardsInventory.createEntityFirst"));
        return;
      }
      return;
    }
    setEditingCard(null);
    setShowCardForm(true);
  };

  const handleEditCard = (card) => {
    setEditingCard(card);
    setShowCardForm(true);
  };

  const handleDeleteCard = async (cardId) => {
    setDeleteConfirmModal({
      show: true,
      type: "card",
      id: cardId,
      message: t("creditCardsInventory.deleteConfirm"),
    });
  };

  const confirmDelete = async () => {
    const { type, id } = deleteConfirmModal;
    try {
      if (type === "entity") {
        // First, delete all cards for this entity
        const entityCards = await getUserCreditCards(
          currentUser.uid,
          "business",
          id
        );
        for (const card of entityCards) {
          await deleteCreditCard(card.id);
        }
        // Then delete the entity
        await deleteBusinessEntity(id);
        // Reload entities and reset selection
        await loadEntities();
        if (selectedEntityId === id) {
          setSelectedEntityId(null);
          setCards([]);
        }
      } else if (type === "card") {
        await deleteCreditCard(id);
        await loadCards(selectedEntityId);
      }
      setDeleteConfirmModal({ show: false, type: "", id: null, message: "" });
    } catch (err) {
      console.error(`Error deleting ${type}:`, err);
      setError(
        type === "entity"
          ? t("creditCardsInventory.failedToDeleteEntity")
          : t("creditCardsInventory.failedToDelete")
      );
      setDeleteConfirmModal({ show: false, type: "", id: null, message: "" });
    }
  };

  const handleCardFormSubmit = async (cardData) => {
    if (!currentUser || !selectedEntityId) return;

    try {
      if (editingCard) {
        await updateCreditCard(editingCard.id, cardData);
      } else {
        // Pass mentorId when creating cards in managed scope
        // Mentor masquerade: use actualUserId (mentor's ID)
        // Client view: use mentorId (viewing mentor's ID)
        // Personal view: null
        const cardMentorId = isMentorMasquerade
          ? actualUserId
          : isInClientView && mentorId
          ? mentorId
          : null;
        
        // Use effectiveUserId for the card owner in masquerade mode
        const cardOwnerUserId = isMentorMasquerade ? effectiveUserId : currentUser.uid;
        
        await createCreditCard(
          cardOwnerUserId,
          { ...cardData, businessEntityId: selectedEntityId },
          cardMentorId
        );
      }
      setShowCardForm(false);
      setEditingCard(null);
      await loadCards(selectedEntityId);
    } catch (err) {
      console.error("Error saving card:", err);
      throw err;
    }
  };

  const handleCardFormCancel = () => {
    setShowCardForm(false);
    setEditingCard(null);
  };

  const selectedEntity = entities.find((e) => e.id === selectedEntityId);

  // Calculate stats
  const totalCards = cards.length;
  const totalCredit = cards.reduce(
    (sum, card) => sum + (card.creditLine || 0),
    0
  );
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "$0";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Filter and sort cards
  const filteredCards = cards
    .filter((card) => {
      const matchesSearch =
        !searchQuery ||
        card.bankName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        card.cardSubType?.toLowerCase().includes(searchQuery.toLowerCase());
      // Derive bankTier from bankName if bankTier is not set
      const cardBankTier = card.bankTier || (card.bankName ? getBankTier(card.bankName) : null);
      const matchesTier = !filterTier || cardBankTier == filterTier;
      return matchesSearch && matchesTier;
    })
    .sort((a, b) => {
      if (sortBy === "creditLimit") {
        return (b.creditLine || 0) - (a.creditLine || 0);
      } else if (sortBy === "bankName") {
        return (a.bankName || "").localeCompare(b.bankName || "");
      } else {
        // dateCreated (default)
        const dateA = a.dateCreated?.toDate
          ? a.dateCreated.toDate()
          : new Date(a.dateCreated || 0);
        const dateB = b.dateCreated?.toDate
          ? b.dateCreated.toDate()
          : new Date(b.dateCreated || 0);
        return dateB - dateA;
      }
    });

  // Redirect to subscription page if user is not subscribed
  useEffect(() => {
    if (!subscriptionLoading && !isSubscribed) {
      history.push("/manage-subscription");
    }
  }, [subscriptionLoading, isSubscribed, history]);

  // Show loading state while checking subscription
  if (subscriptionLoading) {
    return (
      <div className="business-cards">
        <div style={{ padding: "40px", textAlign: "center" }}>
          <div className="spinner"></div>
          <p>{t("creditCardsInventory.checkingSubscriptionStatus")}</p>
        </div>
      </div>
    );
  }

  // Return null if not subscribed (redirect will happen)
  if (!isSubscribed) {
    return null;
  }

  if (showEntityForm) {
    return (
      <div className="business-cards">
        <BusinessEntityForm
          entity={editingEntity}
          onSubmit={handleEntityFormSubmit}
          onCancel={handleEntityFormCancel}
        />
      </div>
    );
  }

  if (showCardForm) {
    return (
      <div className="business-cards">
        <CardForm
          card={editingCard}
          onSubmit={handleCardFormSubmit}
          onCancel={handleCardFormCancel}
          businessEntityId={selectedEntityId}
        />
      </div>
    );
  }

  return (
    <div className="business-cards">
      {error && (
        <div className="error-message">
          {error}
          <button onClick={loadEntities}>
            {t("creditCardsInventory.retry")}
          </button>
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>{t("creditCardsInventory.loadingEntities")}</p>
        </div>
      ) : entities.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🏢</div>
          <h3>{t("creditCardsInventory.noEntitiesYet")}</h3>
          <p>{t("creditCardsInventory.noEntitiesMessage")}</p>
          <button className="btn-primary" onClick={handleAddEntity}>
            {t("creditCardsInventory.createFirstEntity")}
          </button>
        </div>
      ) : (
        <div className="business-cards-layout">
          {/* Sidebar for Business Entities */}
          <div className="business-entities-sidebar">
            <div className="sidebar-header">
              <h3>{t("creditCardsInventory.businessEntities")}</h3>
              <button
                className="btn-add-entity-small"
                onClick={handleAddEntity}
                title={t("creditCardsInventory.addBusinessEntity")}
              >
                +
              </button>
            </div>
            <div className="entities-list">
              {entities.map((entity) => (
                <div
                  key={entity.id}
                  className={`entity-item ${
                    selectedEntityId === entity.id ? "active" : ""
                  }`}
                  onClick={() => setSelectedEntityId(entity.id)}
                >
                  <div className="entity-info">
                    <h4>{entity.businessName}</h4>
                    {entity.businessType && (
                      <span className="entity-type">{entity.businessType}</span>
                    )}
                  </div>
                  <KebabMenu
                    onEdit={() => handleEditEntity(entity)}
                    onDelete={() => handleDeleteEntity(entity.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Main Content Area */}
          <div className="business-cards-main">
            {selectedEntityId ? (
              <>
                {/* Header with Add Card Button */}
                <div className="cards-section-header">
                  <div>
                    <h2>{t("creditCardsInventory.businessCreditCards")}</h2>
                    <p className="entity-context">
                      {t("creditCardsInventory.addingCardTo")}{" "}
                      <strong>
                        {selectedEntity?.businessName ||
                          t("creditCardsInventory.selectedEntity")}
                      </strong>
                    </p>
                  </div>
                  <button className="btn-add-card" onClick={handleAddCard}>
                    {t("creditCardsInventory.addCard")}
                  </button>
                </div>

                {/* Quick Stats */}
                {totalCards > 0 && (
                  <div className="stats-bar">
                    <div className="stat-item">
                      <span className="stat-label">
                        {t("creditCardsInventory.totalCards")}
                      </span>
                      <span className="stat-value">{totalCards}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">
                        {t("creditCardsInventory.totalCredit")}
                      </span>
                      <span className="stat-value">
                        {formatCurrency(totalCredit)}
                      </span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">
                        {t("creditCardsInventory.entities")}
                      </span>
                      <span className="stat-value">{entities.length}</span>
                    </div>
                  </div>
                )}

                {/* Search and Filter */}
                <div className="search-filter-bar">
                  <div className="search-input-wrapper">
                    <span className="search-icon">🔍</span>
                    <input
                      type="text"
                      placeholder={t("creditCardsInventory.searchPlaceholder")}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="search-input"
                    />
                  </div>
                  <select
                    value={filterTier}
                    onChange={(e) => setFilterTier(e.target.value)}
                    className="filter-select"
                  >
                    <option value="">
                      {t("creditCardsInventory.allTiers")}
                    </option>
                    <option value="1">{t("creditCardsInventory.tier1")}</option>
                    <option value="2">{t("creditCardsInventory.tier2")}</option>
                    <option value="3">{t("creditCardsInventory.tier3")}</option>
                  </select>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="filter-select"
                  >
                    <option value="dateCreated">
                      {t("creditCardsInventory.sortByDate")}
                    </option>
                    <option value="creditLimit">
                      {t("creditCardsInventory.sortByCreditLimit")}
                    </option>
                    <option value="bankName">
                      {t("creditCardsInventory.sortByBankName")}
                    </option>
                  </select>
                </div>

                {/* Cards List */}
                <CardList
                  cards={filteredCards}
                  onEdit={handleEditCard}
                  onDelete={handleDeleteCard}
                  isLoading={cardsLoading}
                />
              </>
            ) : (
              <div className="empty-state-centered">
                <div className="empty-icon">💼</div>
                <h3>{t("creditCardsInventory.selectEntity")}</h3>
                <p>{t("creditCardsInventory.selectEntityMessage")}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error Modal */}
      {error && (
        <Modal
          isOpen={true}
          onClose={() => setError(null)}
          type="error"
          title={t("creditCardsInventory.errorTitle", "Error")}
          message={error}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteConfirmModal.show}
        onClose={() =>
          setDeleteConfirmModal({
            show: false,
            type: "",
            id: null,
            message: "",
          })
        }
        type="warning"
        title={t("creditCardsInventory.confirmDeleteTitle", "Confirm Delete")}
        message={deleteConfirmModal.message}
        confirmLabel={t("creditCardsInventory.delete", "Delete")}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default BusinessCards;
